// App.tsx
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { FileSpreadsheet, Download, Trash2, LogOut } from 'lucide-react';
import ProvafiLogo from './assets/Provafi Step 3 (500 x 150 px) (2)-cropped.svg';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useNavigate } from 'react-router-dom';
import { AccountEntry, FinancialData, Project, User, CategoryTemplate } from './types';
import { FileUpload } from './components/file_upload/FileUpload';
import { AccountsTable } from './components/Archive/AccountsTable';
import { AccountsTableAG } from './components/trial_balance/AccountsTableAG';
import { ChartOfAccounts } from './components/ChartOfAccounts';
import { CategoriesManagement } from './components/categories/CategoriesManagement';
import UnifiedDetailedIncomeStatement from './components/financials/UnifiedDetailedIncomeStatement';
import UnifiedDetailedIncomeStatementPercent from './components/financials/UnifiedDetailedIncomeStatementPercent';
// import { UnifiedBalanceSheet } from './components/Archive/UnifiedBalanceSheet'; // Archived component
import { UnifiedDetailedBalanceSheet } from './components/financials/UnifiedDetailedBalanceSheet';
import { UnifiedDetailedNetWorkingCapital } from './components/financials/UnifiedDetailedNetWorkingCapital';
import { Auth } from './components/auth/Auth';
import { ResetPasswordPage } from './components/auth/ResetPasswordPage';
import { initSupabase, getSupabase } from './lib/api/supabase';
import { projectApi, authApi, templateApi } from './lib/api/authApi';
import { saveProjectToBackend, updateProjectInBackend } from './lib/api/api';
import { ProjectList } from './components/dashboard_projects/ProjectList';
import BetaInquiryModal from './components/forms/BetaInquiryModal';
import GeneralInquiryModal from './components/forms/GeneralInquiryModal';
import { suggestCategory, generateCategorySuggestions } from './lib/categories/categories';
import LogRocket from 'logrocket';
import { 
  loadActiveTemplate, 
  saveActiveTemplateId, 
  normalizeFinancialData,
  updateProjectWithNormalizedData,
  ensureProjectHasTemplate,
  initializeActiveTemplate
} from './lib/templates/templateUtils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './components/ui/alert-dialog';
import SettingsPanel from './components/ui/SettingsPanel';
import { UnifiedDetailedCashFlow } from './components/financials/UnifiedDetailedCashFlow';
import { FinancialRatios } from './components/financials/FinancialRatios';
import { syncAllFinancialReportsToSupabase } from './lib/financial/reportSync';
import { AIChatWidget } from './components/ai/AIChatWidget';
import { AITables } from './components/ai/AITables';
import { QoEExecSummary } from './components/ai/QoEExecSummary';
import { Badge } from './components/ui/badge';
import { NewProjectModal } from './components/dashboard_projects/NewProjectModal';
type TabType = 'trial-balance' | 'chart' | 'categories' | 'pl-summary' | 'pl-detail' | 
               'pl-detail-sales' | 'bs-summary' | 'bs-detail' | 'cf-detail' | 'nwc-summary' | 'nwc-detail' | 'ratios' | 'ai-tables' | 'qoe-exec-summary' | 'settings' | 'projects';

interface NavSection {
  title: string;
  tabs: {
    id: TabType;
    label: string;
  }[];
}

interface MainAppProps {
  user: User;
  onLogout: () => void;
  authState: 'idle' | 'initializing' | 'authenticated' | 'unauthenticated';
}

// Add this NavSections definition after your interfaces:
const navSections: NavSection[] = [
  {
    title: 'Data Sources',
    tabs: [
      { id: 'trial-balance', label: 'Trial Balance' },
      { id: 'chart', label: 'Chart of Accounts' },
      { id: 'categories', label: 'Categories' },
    ],
  },
  {
    title: 'QoE Analysis',
    tabs: [
      { id: 'qoe-exec-summary', label: 'QoE Executive Summary' },
    ],
  },
  {
    title: 'Financials',
    tabs: [
      { id: 'pl-detail', label: 'P&L Detail' },
      { id: 'bs-detail', label: 'Balance Sheet Detail' },
      { id: 'cf-detail', label: 'Cash Flow Detail' },
    ],
  },
  {
    title: 'Reports',
    tabs: [
      { id: 'nwc-detail', label: 'NWC Detail' },
      { id: 'pl-detail-sales', label: 'P&L Detail (% Sales)' },
      { id: 'ratios', label: 'Financial Ratios' },
      ...(process.env.NODE_ENV !== 'production' ? [{ id: 'ai-tables' as TabType, label: 'AI Tables' }] : []),
    ],
  },
];

type ErrorBoundaryProps = { children: React.ReactNode };
type ErrorBoundaryState = { hasError: boolean };

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_: Error): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('App error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-xl font-bold text-red-600">Something went wrong</h1>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Create a stable empty financial data object to prevent unnecessary re-renders
const EMPTY_FINANCIAL_DATA: FinancialData = {
  accounts: [],
  mappings: {},
};

// Cache the dynamic import promise to avoid multiple chunk loads
let ensureDefaultTemplateModulePromise: Promise<typeof import('./lib/categories/defaultTemplate')> | null = null;
const ensureDefaultTemplateForUser = async (userId: string) => {
  ensureDefaultTemplateModulePromise ||= import('./lib/categories/defaultTemplate');
  const { ensureDefaultTemplateExists } = await ensureDefaultTemplateModulePromise;
  return ensureDefaultTemplateExists(userId);
};

// Helper for user-scoped localStorage keys
const ACTIVE_TEMPLATE_LS_KEY = (uid: string) => `activeTemplateId:${uid}`;

// Helper to migrate legacy activeTemplateId to user-scoped key
const migrateActiveTemplateId = (userId: string) => {
  const legacyKey = 'activeTemplateId';
  const userScopedKey = ACTIVE_TEMPLATE_LS_KEY(userId);
  
  // If user-scoped key doesn't exist but legacy key does, migrate it
  if (!localStorage.getItem(userScopedKey) && localStorage.getItem(legacyKey)) {
    const legacyValue = localStorage.getItem(legacyKey);
    if (legacyValue) {
      localStorage.setItem(userScopedKey, legacyValue);
      localStorage.removeItem(legacyKey);
      console.log('📦 Migrated legacy activeTemplateId to user-scoped key');
    }
  }
};

// Helper to find and optionally cache default template (DRY principle)
const findDefaultTemplate = async (shouldCache = false, userId?: string): Promise<CategoryTemplate | null> => {
  try {
    const templates = await templateApi.getAll();
    const defaultTemplate = templates.find(t => t.is_default);
    
    if (defaultTemplate && shouldCache && userId) {
      localStorage.setItem(ACTIVE_TEMPLATE_LS_KEY(userId), defaultTemplate.id);
    }
    
    return defaultTemplate || null;
  } catch (error) {
    console.error('Error finding default template:', error);
    return null;
  }
};

function MainApp({ user, onLogout, authState }: MainAppProps) {
  const [suggestionsApplied, setSuggestionsApplied] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('trial-balance');
  const [financialData, setFinancialData] = useState<FinancialData>(EMPTY_FINANCIAL_DATA);
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [isCreatingProject, setIsCreatingProject] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showProjectList, setShowProjectList] = useState(true);
  const [isNormalizing, setIsNormalizing] = useState(false);
  const [activeTemplate, setActiveTemplate] = useState<CategoryTemplate | null>(null);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(true);
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);
  const [hasInitializedProjects, setHasInitializedProjects] = useState(false);
  const [showAdditionalDataModal, setShowAdditionalDataModal] = useState(false);

  // Add ref to access the PL sync function
  const plSyncRef = useRef(null);
  const bsSyncRef = useRef(null);
  const cfSyncRef = useRef(null);
  const nwcSyncRef = useRef(null);


  // Add another ref to track the first render
  const isFirstRenderRef = useRef(true);

  // Add a ref to prevent excessive fetchProjects calls
  const isFetchingProjectsRef = useRef(false);
  const fetchProjectsTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Add a ref to prevent template initialization loop
  const hasInitializedTemplateRef = useRef(false);
  
  // Add loading state for project selection
  const [isSelectingProject, setIsSelectingProject] = useState(false);

  // Track navigation history within the app for intelligent back button behavior
  const appHistoryRef = useRef<string[]>([]);
  const addToAppHistory = useCallback((path: string) => {
    // Only add if it's different from the last entry
    if (appHistoryRef.current[appHistoryRef.current.length - 1] !== path) {
      appHistoryRef.current.push(path);
      // Keep only last 10 entries to prevent memory issues
      if (appHistoryRef.current.length > 10) {
        appHistoryRef.current.shift();
      }
    }
  }, []);

  // Add these lines to handle URL parameters
  const navigate = useNavigate();
  const { tab, projectId } = useParams<{ tab?: string; projectId?: string }>();

  // ✅ REMOVED - History safety net now handled in sequential initialization

  // Load project from URL if needed
  useEffect(() => {
    if (projectId && !selectedProject) {
      const fetchProjectFromId = async () => {
        try {
          // Use secure API instead of direct Supabase
          const data = await projectApi.getById(projectId);
          
          if (!data) throw new Error('Project not found');
          
          if (data && typeof data === 'object' && 'id' in data && 'name' in data && 'financial_data' in data) {
            // DEBUG: Log project period_type when loading from URL
            console.log('[DEBUG] Loading project from URL:', {
              projectId: data.id,
              period_type: (data as any).period_type,
              typeof_period_type: typeof (data as any).period_type
            });
            
            setSelectedProject(data as unknown as Project);
            setFinancialData((data as unknown as Project).financial_data as FinancialData);
            setShowProjectList(false);
            
            // Load the associated template
            if (data.template_id) {
              try {
                const templateData = await templateApi.getById(data.template_id);
                  
                if (templateData && typeof templateData === 'object' && 'id' in templateData && 'categories' in templateData) {
                  setActiveTemplate(templateData as unknown as CategoryTemplate);
                }
              } catch (templateError: any) {
                const status = (templateError?.response?.status ?? templateError?.status) as number | undefined;
                if (status === 404) {
                  console.error('Project template not found, falling back to default:', templateError);
                  // Fallback: Try to load a default template if the project's template doesn't exist
                  try {
                    // Ensure the user has a default template (cached helper)
                    await ensureDefaultTemplateForUser(user.id);
                    
                    const defaultTemplate = await findDefaultTemplate();
                    if (defaultTemplate) {
                      console.log('Using default template as fallback');
                      setActiveTemplate(defaultTemplate);
                      // Update the project to use the default template
                      await projectApi.update(data.id, { template_id: defaultTemplate.id });
                    }
                  } catch (fallbackError) {
                    console.error('Error loading default template fallback:', fallbackError);
                  }
                } else {
                  // Network/auth/server error — log but don't clear template state
                  console.error('Error loading project template (non-404):', templateError);
                }
              }
            }
          }
        } catch (error) {
          console.error('Error fetching project by ID:', error);
          navigate('/dashboard/projects', { replace: true });
        }
      };
      
      fetchProjectFromId();
    }
  }, [projectId]); // Removed navigate and selectedProject to prevent loop
  
  // Simple URL-to-state synchronization only
  useEffect(() => {
    // Skip first render
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }

    // If URL has a tab parameter, sync it to activeTab state
    if (tab && tab !== activeTab) {
      setActiveTab(tab as TabType);
    }

    // Special case: if URL is /dashboard/projects, show project list
    if (tab === 'projects') {
      if (!showProjectList) {
        setShowProjectList(true);
        setSelectedProject(null);
      }
    }
  }, [tab]); // Removed activeTab and showProjectList to prevent loops

  // 🔥 SIMPLIFIED INITIALIZATION - Only fetch projects, templates on-demand
  useEffect(() => {
    if (authState !== 'authenticated' || hasInitializedProjects) {
      return;
    }
    
    const initializeUserData = async () => {
      console.log('📊 Starting user data initialization...');
      try {
        // Step 0: Migrate and validate template IDs from localStorage
        console.log('0️⃣ Validating localStorage template entries...');
        
        // First migrate legacy key to user-scoped key
        migrateActiveTemplateId(user.id);
        
        const userScopedKey = ACTIVE_TEMPLATE_LS_KEY(user.id);
        const storedTemplateId = localStorage.getItem(userScopedKey);
        
        if (storedTemplateId) {
          try {
            // Verify the template actually exists for this user
            await templateApi.getById(storedTemplateId);
            console.log('✅ Retained existing activeTemplateId in localStorage:', storedTemplateId);
          } catch (e: any) {
            const status = (e?.response?.status ?? e?.status) as number | undefined;
            if (status === 404) {
              console.log('🧹 Removing stale activeTemplateId to prevent 404s:', storedTemplateId);
              localStorage.removeItem(userScopedKey);
            } else {
              console.warn('⚠️ Could not verify activeTemplateId; leaving it in place', e);
            }
          }
        }
        
        // Step 1: Fetch projects (critical path)
        console.log('1️⃣ Fetching projects...');
        await fetchProjects();
        
        // Step 2: Initialize user features (non-blocking)
        console.log('2️⃣ Initializing user features...');
        await initializeUserFeatures();
        
        // Step 3: Initialize template (graceful failure)
        console.log('3️⃣ Initializing template...');
        try {
          await initializeTemplateForAuthenticatedUser();
        } catch (templateError) {
          console.warn('⚠️ Template initialization failed, but continuing:', templateError);
          // Don't block the app if templates fail
        }
        
        console.log('🎉 User data initialization complete!');
        setIsLoadingTemplate(false);
      } catch (error) {
        console.error('❌ User data initialization failed:', error);
        setIsLoadingTemplate(false);
      }
    };
    
    initializeUserData();
  }, [authState, hasInitializedProjects, user]);

  // ✅ REMOVED - Template initialization now handled in sequential initialization


  const loadDefaultTemplate = async () => {
    try {
      const templates = await templateApi.getAll();
      const defaultTemplate = templates.find(t => t.is_default);
      
      if (defaultTemplate) {
        setActiveTemplate(defaultTemplate as CategoryTemplate);
      }
    } catch (error) {
      console.error('Error loading default template:', error);
    }
  };

  // 🔥 CONSOLIDATED TEMPLATE INITIALIZATION - Replaces old user-dependent useEffect
  const initializeTemplateForAuthenticatedUser = async () => {
    if (!user || hasInitializedTemplateRef.current) {
      console.log('⏭️ Template already initialized or no user');
      return;
    }
    
    hasInitializedTemplateRef.current = true;
    console.log('🎨 Initializing template for user:', user.id);
    
    try {
      // Small delay to ensure auth token is fully established
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Ensure the user has a default template (cached helper)
      await ensureDefaultTemplateForUser(user.id);
      
      const template = await initializeActiveTemplate();
      if (template) {
        setActiveTemplate(template);
        
        // If a project is selected, ensure it uses the active template
        if (selectedProject) {
          if (!selectedProject.template_id) {
            await ensureProjectHasTemplate(selectedProject.id, template.id);
            setSelectedProject({
              ...selectedProject,
              template_id: template.id
            });
          }
          
          // Normalize data according to the template (only if we have accounts to normalize)
          if (financialData.accounts.length > 0) {
            const normalizedData = normalizeFinancialData(financialData, template);
            setFinancialData(normalizedData);
          }
        }
      } else {
        console.warn('ℹ️ No template found for user even after ensuring default exists — loading default explicitly');
        try {
          const defaultTemplate = await findDefaultTemplate(true, user.id); // Cache it this time
          if (defaultTemplate) {
            setActiveTemplate(defaultTemplate);
            console.log('✅ Successfully loaded default template as fallback');
          } else {
            console.error('❌ No default template found even after explicit creation attempt');
          }
        } catch (error) {
          console.error('❌ Failed to load default template as fallback:', error);
        }
      }
    } catch (error) {
      console.error('❌ Error initializing template:', error);
    }
  };

  // Keep active template aligned with the selected project's template_id.
  // This prevents reload races where global template initialization can override
  // the project-specific template selection.
  useEffect(() => {
    if (!user || !selectedProject?.template_id) return;
    if (activeTemplate?.id === selectedProject.template_id) return;

    let cancelled = false;

    const reconcileProjectTemplate = async () => {
      try {
        const templateData = await templateApi.getById(selectedProject.template_id as string);
        if (!cancelled && templateData) {
          setActiveTemplate(templateData as unknown as CategoryTemplate);
          localStorage.setItem(ACTIVE_TEMPLATE_LS_KEY(user.id), selectedProject.template_id as string);
          console.log('[TemplateReconcile] Applied project template after reload:', selectedProject.template_id);
        }
      } catch (error: any) {
        const status = (error?.response?.status ?? error?.status) as number | undefined;
        if (status === 404) {
          console.warn('[TemplateReconcile] Project template not found (404):', selectedProject.template_id);
        } else {
          console.warn('[TemplateReconcile] Could not reconcile project template:', error);
        }
      }
    };

    reconcileProjectTemplate();

    return () => {
      cancelled = true;
    };
  }, [user, selectedProject?.id, selectedProject?.template_id, activeTemplate?.id]);

  // 🔥 CONSOLIDATED USER FEATURES - Replaces old user-dependent useEffects
  const initializeUserFeatures = async () => {
    if (!user) return;
    
    console.log('🎯 Initializing user features for:', user.id);
    
    try {
      // LogRocket initialization (only in production)
      if (process.env.NODE_ENV === 'production') {
        console.log('📊 Initializing LogRocket for user');
        LogRocket.init('n05sfi/appprovaficom');
        LogRocket.identify(user.id, {
          email: user.email,
        });
      }
      
      // History safety net setup
      const currentPath = window.location.pathname;
      if (currentPath !== '/dashboard/projects') {
        window.history.pushState({}, document.title, window.location.href);
      }
      
      console.log('✅ User features initialized');
    } catch (error) {
      console.error('❌ Error initializing user features:', error);
    }
  };

  const fetchProjects = async () => {
    // Debounced guard - prevent multiple calls within 100ms
    if (isFetchingProjectsRef.current || fetchProjectsTimeoutRef.current) {
      console.log('⏭️ fetchProjects debounced, skipping');
      return;
    }

    // Set a debounce timeout
    fetchProjectsTimeoutRef.current = setTimeout(() => {
      fetchProjectsTimeoutRef.current = null;
    }, 100);

    console.log('📥 Starting fetchProjects...');
    isFetchingProjectsRef.current = true;
    setIsLoadingProjects(true);
    try {
      // Use secure API instead of direct Supabase
      const data = await projectApi.getAll();
  
      if (!data) throw new Error('Failed to fetch projects');
      
      // Filter projects with more lenient criteria - only require id and name
      // financial_data will be ensured to exist to prevent errors
      const filteredProjects = Array.isArray(data) ? (data.filter(d => d && typeof d === 'object' && 'id' in d && 'name' in d) as unknown as Project[]) : [];
      
      // Ensure all projects have financial_data structure to prevent errors
      const projectsWithFinancialData = filteredProjects.map(project => ({
        ...project,
        financial_data: project.financial_data || { accounts: [], categories: {} }
      }));

      // DEBUG: Log project period_type values
      console.log('[DEBUG] fetchProjects - Projects loaded with period_type:', 
        projectsWithFinancialData.map(p => ({ 
          id: p.id, 
          name: p.name, 
          period_type: p.period_type 
        }))
      );
      
      console.log('📊 Setting projects in state:', {
        count: projectsWithFinancialData.length,
        timestamp: new Date().toISOString()
      });
      setProjects(projectsWithFinancialData);
      
      // Only set initialization flag after we've successfully set the projects
      // This ensures we don't show banner while projects array is still empty
      console.log('✅ Setting hasInitializedProjects = true');
      setHasInitializedProjects(true);
    } catch (error) {
      console.error('Error fetching projects:', error);
      alert('Unable to load projects. Please try again.');
      setHasInitializedProjects(true);
    } finally {
      console.log('📤 fetchProjects completed');
      setIsLoadingProjects(false);
      isFetchingProjectsRef.current = false;
      // Clear debounce timeout
      if (fetchProjectsTimeoutRef.current) {
        clearTimeout(fetchProjectsTimeoutRef.current);
        fetchProjectsTimeoutRef.current = null;
      }
    }
  };

  const handleBatchCategoryChange = async (updates: Array<{ accountNumber: string, category: string }>) => {
    if (!selectedProject) return;
  
    try {
      // Create new mappings
      const newMappings = { ...financialData.mappings };
      updates.forEach(update => {
        const account = financialData.accounts.find(a => a.accountNumber === update.accountNumber);
        if (account) {
          newMappings[update.accountNumber] = {
            accountNumber: update.accountNumber,
            name: account.name,
            category: update.category
          };
        }
      });
  
      // Update accounts with new categories
      const newAccounts = financialData.accounts.map(acc => {
        const update = updates.find(u => u.accountNumber === acc.accountNumber);
        return update ? { ...acc, category: update.category } : acc;
      });
  
      const newFinancialData = {
        accounts: newAccounts,
        mappings: newMappings
      };
  
      // Update state and save to database
      setFinancialData(newFinancialData);
      await saveProjectData(newFinancialData);

      // Trigger full report sync after batch category changes (debounced)
      setTimeout(() => {
        if (selectedProject && activeTemplate && newAccounts.length > 0) {
          console.log('[App] Triggering FULL report sync after batch category changes', {
            projectId: selectedProject.id,
            accountsLength: newAccounts.length,
            templateId: activeTemplate.id
          });
          syncAllFinancialReportsToSupabase({
            projectId: selectedProject.id,
            accounts: newAccounts,
            activeTemplate,
            periodType: selectedProject.period_type
          });
        }
      }, 1000);
    } catch (error) {
      console.error('Error in handleBatchCategoryChange:', error);
      throw error;
    }
  };

  // In your MainApp component, update the normalizeCategories function:
  const normalizeCategories = useCallback(async () => {
    if (!activeTemplate || !financialData.accounts.length || isNormalizing) return;

    setIsNormalizing(true);
    try {
      // Create a strict mapping of valid categories
      const validCategories = new Map(
        activeTemplate.categories.map(c => [c.value, c])
      );

      console.log('Valid template categories:', Array.from(validCategories.keys()));

      const updates: Array<{ accountNumber: string, category: string }> = [];

      financialData.accounts.forEach(account => {
        const currentCategory = financialData.mappings[account.accountNumber]?.category || account.category;
        
        // Check for exact match in valid categories
        if (currentCategory && !validCategories.has(currentCategory)) {
          // Try to find matching category based on tags
          const matchingCategory = activeTemplate.categories.find(templateCat => {
            if (!account.name) return false;
            const accountNameLower = account.name.toLowerCase();
            
            // Check if account name includes any of the required tags
            const hasIncludedTag = templateCat.tagsInclude.some(tag => 
              accountNameLower.includes(tag.toLowerCase())
            );
            
            // Check if account name excludes any forbidden tags
            const hasExcludedTag = templateCat.tagsExclude.some(tag => 
              accountNameLower.includes(tag.toLowerCase())
            );
            
            return hasIncludedTag && !hasExcludedTag;
          });

          const newCategory = matchingCategory?.value || 'N/A';
          console.log(`Category update needed for ${account.name}:`, {
            accountNumber: account.accountNumber,
            oldCategory: currentCategory,
            newCategory
          });

          updates.push({
            accountNumber: account.accountNumber,
            category: newCategory
          });
        }
      });

      if (updates.length > 0) {
        console.log(`Applying ${updates.length} category updates:`, updates);
        await handleBatchCategoryChange(updates);
      }
    } catch (error) {
      console.error('Error normalizing categories:', error);
    } finally {
      setIsNormalizing(false);
    }
  }, [activeTemplate, financialData, isNormalizing, handleBatchCategoryChange]);

  const handleCreateProject = async (projectName: string) => {
    setIsCreatingProject(true);
    
    try {
      // Validate user session through secure API
      const sessionValidation = await authApi.validateSession();
      if (!sessionValidation.valid || !sessionValidation.user) {
        alert('Please sign in to create a project');
        return;
      }
  
      // Get active template ID from localStorage or use default  
      const activeTemplateId = localStorage.getItem(ACTIVE_TEMPLATE_LS_KEY(sessionValidation.user.id));
      
      let templateIdToUse = activeTemplateId;
      
      // Validate the template ID exists for this user, or find the default template
      if (templateIdToUse) {
        try {
          // Check if the template ID from localStorage actually exists for this user
          await templateApi.getById(templateIdToUse);
        } catch (error: any) {
          const status = (error?.response?.status ?? error?.status) as number | undefined;
          if (status === 404) {
            console.warn('Template from localStorage not found for this user, using default');
            templateIdToUse = null;
          } else {
            // Network/auth/server error — keep the template ID and throw error
            throw error;
          }
        }
      }
      
      if (!templateIdToUse) {
        try {
          const defaultTemplate = await findDefaultTemplate();
          if (defaultTemplate) {
            templateIdToUse = defaultTemplate.id;
          }
        } catch (error) {
          console.error('Error fetching default template:', error);
        }
      }
  
      const projectId = crypto.randomUUID(); // Generate a unique ID
      console.log('Creating project with ID:', projectId, 'Name:', projectName);
      
      const newProject = {
        id: projectId,
        name: projectName,
        user_id: sessionValidation.user.id,
        template_id: templateIdToUse,
        financial_data: {
          accounts: [],
          mappings: {}
        },
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
  
      // Save project using secure API
      const data = await projectApi.create(newProject);
      console.log('Project created successfully:', data);
  
      // Refresh the projects list from the server to get the updated data
      await fetchProjects();
      
      // Close the modal
      setShowNewProjectModal(false);
      
      // Stay on the projects list instead of navigating to the new project
      navigate('/dashboard/projects', { replace: true });
      
      alert('Project created successfully!');
    } catch (err) {
      console.error('Error creating project:', err);
      alert('Unable to create project. Please try again.');
    } finally {
      setIsCreatingProject(false);
    }
  };

  const handleSelectProject = async (project: Project) => {
    // Prevent multiple simultaneous selections
    if (isSelectingProject) {
      console.log('⏭️ Project selection already in progress, skipping');
      return;
    }
    
    setIsSelectingProject(true);
    try {
      // FETCH FRESH PROJECT DATA FROM DATABASE
      const freshProject = await projectApi.getById(project.id);
      
      // DEBUG: Log fresh project data
      console.log('[DEBUG] handleSelectProject - Fresh project data:', {
        projectId: project.id,
        original_period_type: (project as any).period_type,
        fresh_period_type: (freshProject as any)?.period_type,
        typeof_fresh: typeof (freshProject as any)?.period_type
      });
  
      const projectToUse = freshProject || project;
      
      // Batch state updates to prevent multiple re-renders
      const projectData = projectToUse;
      const financialDataToSet = projectData.financial_data || EMPTY_FINANCIAL_DATA;
      
      // Single state update batch
      setSelectedProject(projectData as Project);
      setFinancialData(financialDataToSet);
      setShowProjectList(false);
      
      // Update active template when selecting a project (after state is set)
      let templateToUse = activeTemplate;
      if (projectToUse.template_id && projectToUse.template_id !== activeTemplate?.id) {
        localStorage.setItem(ACTIVE_TEMPLATE_LS_KEY(user.id), projectToUse.template_id);
        
        // Load the associated template
        try {
          const templateData = await templateApi.getById(projectToUse.template_id);
          if (templateData) {
            templateToUse = templateData;
            setActiveTemplate(templateData);
          }
        } catch (error: any) {
          const status = (error?.response?.status ?? error?.status) as number | undefined;
          if (status === 404) {
            console.error('Project template not found, falling back to default:', error);
            // Fallback: Try to load a default template if the project's template doesn't exist
            try {
              // Ensure the user has a default template (cached helper)
              await ensureDefaultTemplateForUser(user.id);
              
              const defaultTemplate = await findDefaultTemplate();
              if (defaultTemplate) {
                console.log('Using default template as fallback');
                templateToUse = defaultTemplate;
                setActiveTemplate(defaultTemplate);
                // Update the project to use the default template
                await projectApi.update(projectToUse.id, { template_id: defaultTemplate.id });
              }
            } catch (fallbackError) {
              console.error('Error loading default template fallback:', fallbackError);
            }
          } else {
            // Network/auth/server error — log but don't clear template state
            console.error('Error loading project template (non-404):', error);
          }
        }
      }
      
      // Check if project has data and navigate in single operation
      const hasData = financialDataToSet.accounts && 
                      Array.isArray(financialDataToSet.accounts) &&
                      financialDataToSet.accounts.length > 0;
      
      console.log('[Project Selection] Data check details:', {
        hasFinancialData: !!financialDataToSet,
        hasAccounts: !!financialDataToSet.accounts,
        isAccountsArray: Array.isArray(financialDataToSet.accounts),
        accountsLength: financialDataToSet.accounts?.length || 0,
        finalHasData: hasData,
        projectName: projectToUse.name,
        projectId: projectToUse.id
      });
      
      // Additional safety check: if current frontend state has data but fresh DB data doesn't,
      // use the frontend state (this handles race conditions after upload)
      const frontendHasData = financialData.accounts && 
                              Array.isArray(financialData.accounts) &&
                              financialData.accounts.length > 0;
      
      const shouldShowTrialBalance = hasData || frontendHasData;
      
      console.log('[Project Selection] Navigation decision:', {
        dbHasData: hasData,
        frontendHasData,
        finalDecision: shouldShowTrialBalance
      });
      
      // Single navigation operation
      const targetTab: TabType = 'trial-balance';
      const targetPath = `/project/${projectToUse.id}/${targetTab}`;
      
      console.log('[Project Selection] Navigating to:', targetPath);
      setActiveTab(targetTab);
      navigate(targetPath);
      
    } catch (error) {
      console.error('Error in handleSelectProject:', error);
      // Fallback behavior - set trial-balance tab and let navigation useEffect handle URL
      setSelectedProject(project);
      setFinancialData(project.financial_data || EMPTY_FINANCIAL_DATA);
      setShowProjectList(false);
      setActiveTab('trial-balance');
    } finally {
      setIsSelectingProject(false);
    }
  };

  const handleDeleteProject = async (projectId: string) => {
    try {
      await projectApi.delete(projectId);

      setProjects(projects.filter(p => p.id !== projectId));
      if (selectedProject?.id === projectId) {
        setSelectedProject(null);
        setFinancialData(EMPTY_FINANCIAL_DATA);
        setShowProjectList(true);
      }
    } catch (err) {
      console.error('Error deleting project:', err);
      alert('Unable to delete project. Please try again.');
    }
  };

  const handleTemplateSelect = async (template: CategoryTemplate) => {
    try {
      setActiveTemplate(template);
      localStorage.setItem(ACTIVE_TEMPLATE_LS_KEY(user.id), template.id);
      
      if (selectedProject) {
        // Update the project's template_id
        await projectApi.update(selectedProject.id, {
          template_id: template.id,
          updated_at: new Date().toISOString()
        });
        
        // Update selected project in state
        setSelectedProject({
          ...selectedProject,
          template_id: template.id
        });
        
        // Normalize financial data with the new template
        const normalizedData = normalizeFinancialData(financialData, template);
        setFinancialData(normalizedData);
        await updateProjectWithNormalizedData(selectedProject.id, normalizedData, false);
        
        // Trigger full report sync after template change
        setTimeout(() => {
          if (selectedProject && activeTemplate && normalizedData.accounts.length > 0) {
            console.log('[App] Triggering FULL report sync after template change', {
              projectId: selectedProject.id,
              accountsLength: normalizedData.accounts.length,
              templateId: activeTemplate.id
            });
            syncAllFinancialReportsToSupabase({
              projectId: selectedProject.id,
              accounts: normalizedData.accounts,
              activeTemplate,
              periodType: selectedProject.period_type
            });
          }
        }, 500);
      }
    } catch (error) {
      console.error('Error handling template selection:', error);
      alert('Failed to update template. Please try again.');
    }
  };

  const handleUploadAdditionalData = () => {
    setShowAdditionalDataModal(true);
  };

  // Handle tab click with direct navigation
  const handleTabClick = (newTab: TabType) => {
    // Update state and navigate directly
    setActiveTab(newTab);
    const newPath = selectedProject ? `/project/${selectedProject.id}/${newTab}` : `/dashboard/${newTab}`;
    navigate(newPath);
  };

  const handleExcelExport = async () => {
    try {
      console.log('Starting Excel export...');
      console.log('Financial Data:', financialData);
      
      if (!financialData || !financialData.accounts) {
        console.error('No financial data available');
        alert('No data available to export');
        return;
      }
  
      if (!financialData.accounts.length) {
        console.error('No accounts data available');
        alert('No accounts data available to export');
        return;
      }
  
      // Get the current user
      const sessionValidation = await authApi.validateSession();
      
      if (!sessionValidation.valid || !sessionValidation.user) {
        console.error('No authenticated user');
        alert('Please sign in to export data');
        return;
      }
      
      const user = sessionValidation.user;
  
      // Get export settings for this user/project
      const { getExportSettings } = await import('./lib/export/exportSettings');
      const exportSettings = await getExportSettings(user.id, selectedProject?.id);
      
      console.log('Using export settings:', exportSettings);

      // Import and call the export function with settings
      const { exportToExcel } = await import('./lib/export/excelExportExcelJS');
      await exportToExcel(financialData, activeTemplate, exportSettings, selectedProject);
      
      console.log('Excel export completed successfully');
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('Failed to export Excel file. Check console for details.');
    }
  };

  const saveProjectData = async (newFinancialData: FinancialData) => {
    if (!selectedProject) return;
    
    setIsSaving(true);
    try {
      // Save via secure API
      await projectApi.update(selectedProject.id, {
        financial_data: newFinancialData,
        updated_at: new Date().toISOString()
      });
  
      return true;
    } catch (error) {
      console.error('Error saving project data:', error);
      alert('Unable to save changes. Please try again.');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  // Memoized chart accounts data for ChartOfAccounts component
  const chartAccounts = useMemo(() => {
    const result = [...new Set(financialData.accounts.map(account => account.accountNumber))]
      .map(accountNumber => {
        const account = financialData.accounts.find(a => a.accountNumber === accountNumber);
        const category = financialData.mappings[accountNumber]?.category || account?.category || 'N/A';
        const templateCategory = activeTemplate?.categories.find(c => c.value === category);

        return {
          name: account?.name || '',
          accountNumber: accountNumber,
          category: category,
          type: templateCategory?.type || 'n/a'
        };
      })
      .filter(account => account.name && account.accountNumber);
    
    // Debug: Chart accounts recalculated
    
    return result;
  }, [financialData.accounts, financialData.mappings, activeTemplate]);

  const handleRenameProject = async (projectId: string, newName: string) => {
    try {
      // Find the project to update
      const projectIndex = projects.findIndex(p => p.id === projectId);
      if (projectIndex === -1) return;
  
      // Create updated project list
      const updatedProjects = [...projects];
      updatedProjects[projectIndex] = {
        ...updatedProjects[projectIndex],
        name: newName,
        updated_at: new Date().toISOString()
      };
  
      // Update state
      setProjects(updatedProjects);
      
      // If this is the selected project, update that too
      if (selectedProject?.id === projectId) {
        setSelectedProject({
          ...selectedProject,
          name: newName,
          updated_at: new Date().toISOString()
        });
      }
    } catch (error) {
      console.error('Error handling project rename:', error);
      alert('Failed to update project name. Please try again.');
    }
  };

  // Add this debugging function to App.tsx 
  const debugDataState = (label: string, data: any) => {
    if (data?.accounts) {
      const jan25Data = data.accounts.filter(acc => acc.month === 'Jan-25');
      console.log(`[DEBUG ${label}]`, {
        totalAccounts: data.accounts.length,
        jan25Count: jan25Data.length,
        jan25Sample: jan25Data.slice(0, 3),
        allMonths: [...new Set(data.accounts.map(acc => acc.month))].sort()
      });
    }
  };

  // FIXED: Function to save data to database AND update local UI state
  // In App.tsx, replace the existing saveDataToDatabase function with this:
  // COMPLETE FIXED VERSION - Replace the incomplete saveDataToDatabase function in App.tsx
  // COMPLETE FIXED VERSION - Replace the existing saveDataToDatabase function in App.tsx
  const saveDataToDatabase = async (accounts: AccountEntry[], isAlreadyMerged: boolean = false, detectedPeriodType?: 'monthly' | 'quarterly' | 'annual') => {
    if (!activeTemplate || !selectedProject) {
      console.warn('[saveDataToDatabase] Missing activeTemplate or selectedProject');
      return;
    }
    
    console.log('[saveDataToDatabase] Starting with:', {
      accountsLength: accounts.length,
      isAlreadyMerged,
      selectedProjectId: selectedProject.id,
      sampleAccounts: accounts.slice(0, 3)
    });
    
    // Debug function to trace data state
    const debugDataState = (label: string, data: any) => {
      if (data?.accounts) {
        const jan25Data = data.accounts.filter(acc => acc.month === 'Jan-25');
        const allMonths = [...new Set(data.accounts.map(acc => acc.month))].sort();
        console.log(`[DEBUG ${label}]`, {
          totalAccounts: data.accounts.length,
          jan25Count: jan25Data.length,
          jan25Sample: jan25Data.slice(0, 3),
          allMonths,
          accountNumbers: [...new Set(data.accounts.map(acc => acc.accountNumber))].length
        });
      }
    };
    
    debugDataState('Input to saveDataToDatabase', { accounts });
    
    try {
      let newFinancialData: FinancialData;
      
      if (isAlreadyMerged) {
        // When data is already merged from FileUpload, the accounts contain all merged data
        // We need to preserve existing mappings and only update them for new accounts
        const newMappings = { ...financialData.mappings };
        
        console.log('[saveDataToDatabase] Processing already-merged data, existing mappings:', Object.keys(newMappings).length);
        
        // Add mappings for any accounts that don't have them yet
        accounts.forEach(account => {
          if (!newMappings[account.accountNumber]) {
            newMappings[account.accountNumber] = {
              accountNumber: account.accountNumber,
              name: account.name,
              category: account.category || 'N/A'
            };
          }
        });
        
        newFinancialData = {
          accounts,
          mappings: newMappings
        };
        
        console.log('[saveDataToDatabase] Created FinancialData for already-merged data:', {
          accountCount: accounts.length,
          mappingCount: Object.keys(newMappings).length
        });
      } else {
        // Normal case - data not pre-merged
        newFinancialData = {
          accounts,
          mappings: { ...financialData.mappings }
        };
        
        console.log('[saveDataToDatabase] Created FinancialData for new data:', {
          accountCount: accounts.length,
          mappingCount: Object.keys(newFinancialData.mappings).length
        });
      }
      
      debugDataState('Before normalization', newFinancialData);
      
      // Apply the normalizeFinancialData function only if data is not already merged
      const normalizedData = isAlreadyMerged ? newFinancialData : normalizeFinancialData(newFinancialData, activeTemplate);
      
      console.log('[saveDataToDatabase] Data normalization result:', {
        isAlreadyMerged,
        skippedNormalization: isAlreadyMerged,
        finalAccountCount: normalizedData.accounts.length
      });
      
      debugDataState('After normalization', normalizedData);
      
      // Generate category suggestions if data is not already merged
      if (!isAlreadyMerged) {
        console.log('[saveDataToDatabase] Generating category suggestions...');
        // Convert AccountEntry[] to ChartAccount[] for suggestions
        const chartAccounts = [...new Set(normalizedData.accounts.map(account => account.accountNumber))]
          .map(accountNumber => {
            const account = normalizedData.accounts.find(a => a.accountNumber === accountNumber);
            const category = normalizedData.mappings[accountNumber]?.category || account?.category || 'N/A';
            const templateCategory = activeTemplate?.categories.find(c => c.value === category);
            return {
              name: account?.name || '',
              accountNumber: accountNumber,
              category: category,
              type: templateCategory?.type || 'n/a'
            };
          })
          .filter(account => account.name && account.accountNumber);
        
        const suggestions = generateCategorySuggestions(chartAccounts, activeTemplate?.categories);
        
        if (suggestions.length > 0) {
          console.log('[saveDataToDatabase] Applying category suggestions:', suggestions.length);
          
          // Apply suggestions to accounts
          const updatedAccounts = normalizedData.accounts.map(acc => {
            const suggestion = suggestions.find(s => s.accountNumber === acc.accountNumber);
            return suggestion ? { ...acc, category: suggestion.category } : acc;
          });
          
          // Update mappings with suggestions
          const updatedMappings = { ...normalizedData.mappings };
          suggestions.forEach(({ accountNumber, category }) => {
            const account = accounts.find(a => a.accountNumber === accountNumber);
            if (account) {
              updatedMappings[accountNumber] = {
                accountNumber,
                name: account.name,
                category
              };
            }
          });
          
          const suggestedData = {
            accounts: updatedAccounts,
            mappings: updatedMappings
          };
          
          debugDataState('After applying suggestions', suggestedData);
          
          // Save to database with suggestions
          console.log('[saveDataToDatabase] About to save suggested data to backend:', {
            accountCount: suggestedData.accounts.length,
            isAlreadyMerged,
            sampleAccounts: suggestedData.accounts.slice(0, 3)
          });
          
          const saveSuccess = await updateProjectWithNormalizedData(selectedProject.id, suggestedData, isAlreadyMerged, detectedPeriodType);
          console.log('[saveDataToDatabase] Backend save result (with suggestions):', saveSuccess);
          
          if (saveSuccess) {
            setFinancialData(suggestedData);
            console.log('[saveDataToDatabase] Frontend state updated with suggested data');
            
            // Update the selected project's financial_data AND period_type to keep it in sync
            setSelectedProject(prev => prev ? { 
              ...prev, 
              financial_data: suggestedData,
              // CRITICAL FIX: Update period_type in frontend state after backend save
              ...(detectedPeriodType && { period_type: detectedPeriodType })
            } : null);
            
            console.log('[saveDataToDatabase] Updated selectedProject with period_type (suggestions path):', detectedPeriodType);
            
            await fetchProjects();
            
            // Force navigation to trial-balance tab after successful data save
            console.log('[saveDataToDatabase] Forcing navigation to trial-balance tab');
            setActiveTab('trial-balance');
            if (selectedProject) {
              // Add small delay to ensure state update completes first
              setTimeout(() => {
                navigate(`/project/${selectedProject.id}/trial-balance`);
              }, 10);
            }
            
            // Close the additional data modal if it's open
            setShowAdditionalDataModal(false);
            
            // Trigger report sync
            setTimeout(() => {
              if (selectedProject && activeTemplate && suggestedData.accounts.length > 0) {
                syncAllFinancialReportsToSupabase({
                  projectId: selectedProject.id,
                  accounts: suggestedData.accounts,
                  activeTemplate,
                  periodType: selectedProject.period_type
                });
              }
            }, 500);
            
            console.log('[saveDataToDatabase] Data saved to database (with suggestions) AND UI state updated');
            return;
          } else {
            console.error('[saveDataToDatabase] Backend save failed (with suggestions) - not updating frontend state');
            alert('Failed to save data. Please try again.');
            return;
          }
        }
      }
      
      // No suggestions OR already merged - save normalized data to database
      console.log('[saveDataToDatabase] About to save data to backend (no suggestions):', {
        accountCount: normalizedData.accounts.length,
        isAlreadyMerged,
        sampleAccounts: normalizedData.accounts.slice(0, 3)
      });
      
      const saveSuccess = await updateProjectWithNormalizedData(selectedProject.id, normalizedData, isAlreadyMerged, detectedPeriodType);
      console.log('[saveDataToDatabase] Backend save result (no suggestions):', saveSuccess);
      
      if (saveSuccess) {
        // Update frontend state ONLY after successful backend save
        setFinancialData(normalizedData);
        console.log('[saveDataToDatabase] Frontend state updated with normalized data');
        
        debugDataState('Final frontend state', normalizedData);
        
        // Update the selected project's financial_data AND period_type to keep it in sync
        setSelectedProject(prev => prev ? { 
          ...prev, 
          financial_data: normalizedData,
          // CRITICAL FIX: Update period_type in frontend state after backend save
          ...(detectedPeriodType && { period_type: detectedPeriodType })
        } : null);
        
        console.log('[saveDataToDatabase] Updated selectedProject with period_type:', detectedPeriodType);
        
        await fetchProjects();
        
        // Force navigation to trial-balance tab after successful data save
        console.log('[saveDataToDatabase] Forcing navigation to trial-balance tab');
        setActiveTab('trial-balance');
        if (selectedProject) {
          // Add small delay to ensure state update completes first
          setTimeout(() => {
            navigate(`/project/${selectedProject.id}/trial-balance`);
          }, 10);
        }
        
        // Close the additional data modal if it's open
        setShowAdditionalDataModal(false);
        
        // Trigger report sync
        setTimeout(() => {
          if (selectedProject && activeTemplate && normalizedData.accounts.length > 0) {
            syncAllFinancialReportsToSupabase({
              projectId: selectedProject.id,
              accounts: normalizedData.accounts,
              activeTemplate,
              periodType: selectedProject.period_type
            });
          }
        }, 500);
        
        console.log('[saveDataToDatabase] Data saved to database (no suggestions) AND UI state updated');
      } else {
        console.error('[saveDataToDatabase] Backend save failed (no suggestions) - not updating frontend state');
        alert('Failed to save data. Please try again.');
      }
      
    } catch (error) {
      console.error('[saveDataToDatabase] Error processing data:', error);
      alert('Failed to save data. Please try again.');
    }
  };

  // Add this function to App.tsx for debugging
  const debugBackendState = async () => {
    if (!selectedProject) return;
    
    try {
      const data = await projectApi.getById(selectedProject.id);
        
      if (!data) {
        console.error('Debug: Error fetching backend state: project not found');
        return;
      }
      
      console.log('DEBUG: Backend financial_data state:', {
        accountCount: data.financial_data?.accounts?.length || 0,
        mappingsCount: Object.keys(data.financial_data?.mappings || {}).length,
        sampleAccount: data.financial_data?.accounts?.[0]
      });
      
      console.log('DEBUG: Frontend financial_data state:', {
        accountCount: financialData.accounts?.length || 0,
        mappingsCount: Object.keys(financialData.mappings || {}).length,
        sampleAccount: financialData.accounts?.[0]
      });
      
    } catch (error) {
      console.error('Debug: Error during state check:', error);
    }
  };

  

  // Call this function after uploading additional data to check sync
  
  // Update the handleDataLoaded function
  const handleDataLoaded = async (accounts: AccountEntry[], detectedPeriodType?: 'monthly' | 'quarterly' | 'annual') => {
    if (!activeTemplate) return;
    
    // Log detected period type
    if (detectedPeriodType) {
      console.log('📊 [App] Detected period type from file upload:', detectedPeriodType);
    }
    
    // Reset suggestions applied flag when new data is loaded
    setSuggestionsApplied(false);
    
    // Safety check: if this is being called and we have existing data, this might be from the additional data modal
    // In additional data mode, this should not directly replace existing data - it should go through conflict resolution
    if (financialData.accounts.length > 0 && accounts.length > 0) {
      console.warn('[App] handleDataLoaded called with existing data - this should go through conflict resolution in additional data mode');
    }
    
    try {
      const newFinancialData = {
        accounts,
        mappings: { ...financialData.mappings }
      };
      
      // Apply the normalizeFinancialData function
      const normalizedData = normalizeFinancialData(newFinancialData, activeTemplate);
      
      // Generate and apply category suggestions automatically
      const chartAccounts = [...new Set(accounts.map(account => account.accountNumber))]
        .map(accountNumber => {
          const account = accounts.find(a => a.accountNumber === accountNumber);
          const category = financialData.mappings[accountNumber]?.category || account?.category || 'N/A';
          const templateCategory = activeTemplate.categories.find(c => c.value === category);
          return {
            name: account?.name || '',
            accountNumber: accountNumber,
            category: category,
            type: templateCategory?.type || 'n/a'
          };
        })
        .filter(account => account.name && account.accountNumber);
      
      const suggestions = generateCategorySuggestions(chartAccounts, activeTemplate?.categories);
      
      if (suggestions.length > 0) {
        // Apply the suggestions to normalized data
        const updatedAccounts = normalizedData.accounts.map(acc => {
          const suggestion = suggestions.find(s => s.accountNumber === acc.accountNumber);
          return suggestion ? { ...acc, category: suggestion.category } : acc;
        });
        
        // Update mappings with suggestions
        const updatedMappings = { ...normalizedData.mappings };
        suggestions.forEach(({ accountNumber, category }) => {
          const account = accounts.find(a => a.accountNumber === accountNumber);
          if (account) {
            updatedMappings[accountNumber] = {
              accountNumber,
              name: account.name,
              category
            };
          }
        });
        
        const suggestedData = {
          accounts: updatedAccounts,
          mappings: updatedMappings
        };
        
        // Set the data with suggestions applied
        setFinancialData(suggestedData);
        setSuggestionsApplied(true);
        
        if (selectedProject) {
          await updateProjectWithNormalizedData(selectedProject.id, suggestedData, false, detectedPeriodType);
          
          // Update the selected project's financial_data AND period_type to keep it in sync
          setSelectedProject(prev => prev ? { 
            ...prev, 
            financial_data: suggestedData,
            // CRITICAL FIX: Update period_type in frontend state after backend save
            ...(detectedPeriodType && { period_type: detectedPeriodType })
          } : null);
          
          console.log('[saveDataToDatabase] Updated selectedProject with period_type (apply suggestions):', detectedPeriodType);
          
          // **FIX: Refresh the project in the projects list to reflect the updated data**
          await fetchProjects();
          
          // Trigger full report sync after data is loaded and processed
          setTimeout(() => {
            if (selectedProject && activeTemplate && suggestedData.accounts.length > 0) {
              console.log('[App] Triggering FULL report sync after data load with suggestions', {
                projectId: selectedProject.id,
                accountsLength: suggestedData.accounts.length,
                templateId: activeTemplate.id
              });
              syncAllFinancialReportsToSupabase({
                projectId: selectedProject.id,
                accounts: suggestedData.accounts,
                activeTemplate,
                periodType: detectedPeriodType || 'monthly'
              });
            }
          }, 500);          
        }
        return;
      }
      
      // If no suggestions applied, just set the normalized data
      setFinancialData(normalizedData);
      
      if (selectedProject) {
        await updateProjectWithNormalizedData(selectedProject.id, normalizedData, false, detectedPeriodType);
        
        // Update the selected project's financial_data and period_type to keep it in sync
        setSelectedProject(prev => prev ? { 
          ...prev, 
          financial_data: normalizedData,
          period_type: detectedPeriodType || prev.period_type
        } : null);
        
        // **FIX: Refresh the project in the projects list to reflect the updated data**
        await fetchProjects();
        
        // Trigger full report sync after data is loaded (no suggestions)
        setTimeout(() => {
          if (selectedProject && activeTemplate && normalizedData.accounts.length > 0) {
            console.log('[App] Triggering FULL report sync after data load (no suggestions)', {
              projectId: selectedProject.id,
              accountsLength: normalizedData.accounts.length,
              templateId: activeTemplate.id,
              selectedProjectPeriodType: selectedProject.period_type,
              detectedPeriodTypeFromUpload: detectedPeriodType
            });
            syncAllFinancialReportsToSupabase({
              projectId: selectedProject.id,
              accounts: normalizedData.accounts,
              activeTemplate,
              periodType: detectedPeriodType || 'monthly'
            });
          }
        }, 500);
      }
      
      // Force navigation to trial-balance tab after successful data load
      console.log('[App] Forcing navigation to trial-balance tab after data load');
      setActiveTab('trial-balance');
      if (selectedProject) {
        // Add small delay to ensure state update completes first
        setTimeout(() => {
          navigate(`/project/${selectedProject.id}/trial-balance`);
        }, 10);
      }
      
      // Note: Report sync is already triggered above in the setTimeout block (line 1465-1477)
      // Removed duplicate immediate sync to prevent double-processing
    } catch (error) {
      console.error('Error handling data load:', error);
      alert('Failed to process uploaded data. Please try again.');
    }
  };

  // Update the handleCategoryChange function
  const handleCategoryChange = async (accountNumber: string, category: string) => {
    if (!activeTemplate) return;
    
    try {
      const account = financialData.accounts.find(a => a.accountNumber === accountNumber);
      if (!account) return;

      const validCategories = new Set(activeTemplate.categories.map(c => c.value));
      const finalCategory = validCategories.has(category) ? category : 'N/A';

      const newMappings = {
        ...financialData.mappings,
        [accountNumber]: {
          accountNumber,
          name: account.name,
          category: finalCategory
        }
      };

      const newFinancialData = {
        accounts: financialData.accounts.map(acc => 
          acc.accountNumber === accountNumber 
            ? { ...acc, category: finalCategory } 
            : acc
        ),
        mappings: newMappings
      };

      setFinancialData(newFinancialData);

      if (selectedProject) {
        await updateProjectWithNormalizedData(selectedProject.id, newFinancialData, false);
        
        // Trigger full report sync after category change (debounced)
        setTimeout(() => {
          if (selectedProject && activeTemplate && newFinancialData.accounts.length > 0) {
            console.log('[App] Triggering FULL report sync after category change', {
              projectId: selectedProject.id,
              accountsLength: newFinancialData.accounts.length,
              templateId: activeTemplate.id
            });
                      syncAllFinancialReportsToSupabase({
            projectId: selectedProject.id,
            accounts: newFinancialData.accounts,
            activeTemplate,
            periodType: selectedProject.period_type
          });
          }
        }, 1000);
      }
    } catch (error) {
      console.error('Error updating category:', error);
      alert('Failed to update category. Please try again.');
    }
  };
  
  // Add loading state check here, before the project list check
  if (isLoadingTemplate) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading templates...</p>
        </div>
      </div>
    );
  }

  if (showProjectList) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Top Bar */}
        <div className="bg-white border-b border-gray-200 px-4 py-4 w-full">
          <div className="flex justify-between items-center">
            <div>
              {/* Empty left side for consistency */}
            </div>
            <div className="flex space-x-4">
              <button
                onClick={() => setShowNewProjectModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                New Project
              </button>
              <button
                onClick={onLogout}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Log Out
              </button>
            </div>
          </div>
        </div>

        <div className="px-4 sm:px-6 lg:px-8 py-8">
          {/* Projects List */}
          <ProjectList
            projects={projects}
            onSelect={handleSelectProject}
            onDelete={handleDeleteProject}
            onLogout={onLogout}
            onNewProject={() => setShowNewProjectModal(true)}
            onRename={handleRenameProject}
            isLoading={isLoadingProjects || isSelectingProject}
            hasInitialized={hasInitializedProjects}
          />
        </div>

        {/* New Project Modal */}
        <NewProjectModal
          isOpen={showNewProjectModal}
          onClose={() => setShowNewProjectModal(false)}
          onCreateProject={handleCreateProject}
          isCreating={isCreatingProject}
        />
      </div>
    );
  }
                
  console.log('Rendering main app view', {
    accountsLength: financialData.accounts.length,
    activeTab,
    activeTemplate: !!activeTemplate
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Conditionally render the left navigation */}
      {financialData.accounts.length > 0 && (
        <div className="w-64 bg-[#1a237e] text-white h-screen flex-shrink-0 fixed left-0 flex flex-col">
          <div className="p-4 overflow-y-auto flex-1">
            <div className="mb-8">
              <div className="mb-4">
                <img 
                  src={ProvafiLogo} 
                  alt="Provafi" 
                  className="h-8 w-auto"
                />
              </div>
              <h1 className="text-lg font-semibold truncate">
                {selectedProject ? selectedProject.name : 'Provafi'}
              </h1>
            </div>
            {/* Render nav sections dynamically */}
            {navSections.map(section => (
              <div className="mb-8" key={section.title}>
                <h2 className="text-xs font-semibold uppercase tracking-wider mb-4 text-blue-200">
                  {section.title}
                </h2>
                <nav className="space-y-1">
                  {section.tabs.map((tab, idx) => {
                    // QoE Exec Summary custom rendering
                    if (tab.id === 'qoe-exec-summary') {
                      const isProjectContextIncomplete = selectedProject && 
                        (!selectedProject.industry?.trim() || !selectedProject.business_description?.trim());
                      
                      return (
                        <button
                          key={tab.id}
                          onClick={() => handleTabClick(tab.id)}
                          className={`w-full flex items-center px-3 py-2 text-sm rounded-md transition-colors ${
                            activeTab === tab.id
                              ? 'bg-blue-900 text-white'
                              : 'text-blue-100 hover:bg-blue-800'
                          }`}
                        >
                          <span className="flex items-center justify-between w-full">
                            <span>{tab.label}</span>
                            {isProjectContextIncomplete && (
                              <span className="ml-2 w-2 h-2 bg-orange-500 rounded-full animate-pulse"></span>
                            )}
                          </span>
                        </button>
                      );
                    }
                    // Default rendering for other tabs
                    return (
                      <button
                        key={tab.id}
                        onClick={() => handleTabClick(tab.id)}
                        className={`w-full flex items-center px-3 py-2 text-sm rounded-md transition-colors ${
                          activeTab === tab.id
                            ? 'bg-blue-900 text-white'
                            : 'text-blue-100 hover:bg-blue-800'
                        }`}
                      >
                        {tab.label}
                      </button>
                    );
                  })}
                </nav>
              </div>
            ))}
            
            {/* Settings section - moved into scrollable area with visual separation */}
            <div className="mt-8 pt-8 border-t border-blue-900">
              <h2 className="text-xs font-semibold uppercase tracking-wider mb-4 text-blue-200">
                Settings
              </h2>
              <nav className="space-y-1">
                <button
                  onClick={() => handleTabClick('settings')}
                  className={`w-full flex items-center px-3 py-2 text-sm rounded-md transition-colors ${
                    activeTab === 'settings'
                      ? 'bg-blue-900 text-white'
                      : 'text-blue-100 hover:bg-blue-800'
                  }`}
                >
                  <span className="mr-2">⚙️</span> 
                  <span className="flex items-center justify-between w-full">
                    <span>Settings</span>
                    {(activeTab === 'qoe-exec-summary' || activeTab === 'settings') && selectedProject && 
                     (!selectedProject.industry?.trim() || !selectedProject.business_description?.trim()) && (
                      <span className="ml-2 w-2 h-2 bg-orange-500 rounded-full animate-pulse"></span>
                    )}
                  </span>
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className={`flex-1 overflow-auto ${financialData.accounts.length > 0 ? 'ml-64' : ''}`}>
        {/* Top Bar */}
        <div className="bg-white border-b border-gray-200 px-4 py-4 w-full">
          <div className="flex justify-between items-center">
            <div>
              {isSaving && (
                <span className="text-sm text-gray-500">Saving...</span>
              )}
              {isSelectingProject && (
                <span className="text-sm text-blue-500">Loading project...</span>
              )}
            </div>
            <div className="flex space-x-4">
            <button
              onClick={() => {
                setShowProjectList(true);
                fetchProjects().then(() => {
                  navigate('/dashboard/projects');
                  setSelectedProject(null);
                });
              }}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Back to Projects
            </button>
              {financialData.accounts.length > 0 && (
                <button
                  onClick={handleExcelExport}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                >
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  Export Excel
                </button>
              )}
              <button
                onClick={onLogout}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Log Out
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className={`${financialData.accounts.length === 0 ? 'w-full' : 'p-6 w-full'}`}>
          {financialData.accounts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <FileUpload 
                onDataLoaded={handleDataLoaded} 
                onDataProcessed={saveDataToDatabase}
                selectedProject={selectedProject}/>
            </div>
          ) : (
            <>
              {activeTab === 'trial-balance' ? (
                <AccountsTableAG
                  accounts={financialData.accounts}
                  activeTemplate={activeTemplate}
                  onUploadAdditionalData={handleUploadAdditionalData}
                  mappings={financialData.mappings}
                  onAccountsChange={(updatedAccounts) => {
                    const newFinancialData = {
                      ...financialData,
                      accounts: updatedAccounts
                    };
                    setFinancialData(newFinancialData);
                    if (selectedProject) {
                      saveProjectData(newFinancialData);
                    }
                  }}
                />
              ) : activeTab === 'chart' ? (
                <ChartOfAccounts
                  accounts={chartAccounts}
                  onCategoryChange={handleCategoryChange}
                  onBatchCategoryChange={handleBatchCategoryChange}
                  suggestionsApplied={suggestionsApplied}
                  onSuggestionsApplied={() => setSuggestionsApplied(true)}
                  activeTemplate={activeTemplate}
                  isActive={activeTab === 'chart'}
                  onUploadAdditionalData={handleUploadAdditionalData}
                />
              ) : activeTab === 'categories' ? (
                <CategoriesManagement
                  onUpdateCategories={(updatedCategories) => {
                    if (activeTemplate) {
                      const updatedTemplate = {
                        ...activeTemplate,
                        categories: updatedCategories
                      };
                      setActiveTemplate(updatedTemplate);
                      
                      const normalizedData = normalizeFinancialData(financialData, updatedTemplate);
                      setFinancialData(normalizedData);
                      
                      if (selectedProject) {
                        updateProjectWithNormalizedData(selectedProject.id, normalizedData, false);
                        
                        // Trigger full report sync after category updates
                        setTimeout(() => {
                          if (selectedProject && activeTemplate && normalizedData.accounts.length > 0) {
                            console.log('[App] Triggering FULL report sync after category updates', {
                              projectId: selectedProject.id,
                              accountsLength: normalizedData.accounts.length,
                              templateId: activeTemplate.id
                            });
                            syncAllFinancialReportsToSupabase({
                              projectId: selectedProject.id,
                              accounts: normalizedData.accounts,
                              activeTemplate,
                              periodType: selectedProject.period_type
                            });
                          }
                        }, 500);
                      }
                    }
                  }}
                  onTemplateSelect={handleTemplateSelect}
                  activeTemplate={activeTemplate}
                />
              ) : activeTab === 'pl-detail' ? (
                <UnifiedDetailedIncomeStatement 
                  ref={plSyncRef}
                  accounts={financialData.accounts} 
                  activeTemplate={activeTemplate}
                  selectedProject={selectedProject}
                />
              ) : activeTab === 'pl-detail-sales' ? (
                <UnifiedDetailedIncomeStatementPercent accounts={financialData.accounts} activeTemplate={activeTemplate} selectedProject={selectedProject} />
              ) : activeTab === 'bs-detail' ? (
                <UnifiedDetailedBalanceSheet 
                  ref={bsSyncRef}
                  accounts={financialData.accounts} 
                  activeTemplate={activeTemplate}
                  selectedProject={selectedProject}
                />
              ) : activeTab === 'cf-detail' ? (
                <UnifiedDetailedCashFlow
                ref={cfSyncRef}
                  accounts={financialData.accounts} 
                  activeTemplate={activeTemplate}
                  selectedProject={selectedProject}
                />
              ) : activeTab === 'nwc-detail' ? (
                <UnifiedDetailedNetWorkingCapital
                  ref={nwcSyncRef}
                  accounts={financialData.accounts}
                  activeTemplate={activeTemplate}
                  selectedProject={selectedProject}
                />
              ) : activeTab === 'ratios' ? (
                <FinancialRatios 
                  accounts={financialData.accounts} 
                  activeTemplate={activeTemplate} 
                  selectedProject={selectedProject}
                />
              ) : activeTab === 'ai-tables' ? (
                <AITables 
                  accounts={financialData.accounts}
                  selectedProject={selectedProject}
                  periodType={selectedProject?.period_type || 'monthly'}
                />
              ) : activeTab === 'qoe-exec-summary' ? (
                <QoEExecSummary 
                  accounts={financialData.accounts}
                  selectedProject={selectedProject}
                  activeTemplate={activeTemplate}
                  periodType={selectedProject?.period_type || 'monthly'}
                  onNavigateToSettings={() => handleTabClick('settings')}
                />
              ) : activeTab === 'settings' ? (
                <SettingsPanel 
                  user={user}
                  selectedProject={selectedProject}
                  onSetProjectExportSettings={(settingsId: string) => {
                    if (selectedProject) {
                      setSelectedProject({ ...selectedProject, export_settings: settingsId });
                    }
                  }}
                  onProjectUpdated={(updatedProject) => {
                    setSelectedProject(updatedProject);
                  }}
                />
              ) : (
                <UnifiedDetailedIncomeStatement 
                  accounts={financialData.accounts} 
                  activeTemplate={activeTemplate}
                  selectedProject={selectedProject}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* Additional Data Upload Modal */}
      {showAdditionalDataModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Upload Additional Data</h2>
              <button
                onClick={() => setShowAdditionalDataModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            <FileUpload
              onDataLoaded={handleDataLoaded}
              onDataProcessed={saveDataToDatabase}
              selectedProject={selectedProject}
              isAdditionalData={true}
              existingData={financialData.accounts}
              onModalClose={() => setShowAdditionalDataModal(false)}
            />
          </div>
        </div>
      )}

      {/* 🚀 ADD THE AI CHAT WIDGET HERE - RIGHT BEFORE THE CLOSING </div> */}
      <AIChatWidget
        accounts={financialData.accounts}
        activeTab={activeTab}
        activeTemplate={activeTemplate}
        financialData={financialData}
        selectedProject={selectedProject}
        periodType={selectedProject?.period_type || 'monthly'}
        hideWidget={financialData.accounts.length === 0 || showAdditionalDataModal}
      />
    
    </div>
  );
}

// Landing Page Component
interface LandingPageProps {
  onBetaInquiryClick: () => void;
  onGeneralInquiryClick: () => void;
}

const LandingPage: React.FC<LandingPageProps> = ({ onBetaInquiryClick, onGeneralInquiryClick }: LandingPageProps) => {
  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 flex items-center justify-center">
        <Auth 
          onAuthSuccess={() => {}} 
          onBetaInquiryClick={onBetaInquiryClick}
          onGeneralInquiryClick={onGeneralInquiryClick}
        />
      </div>
    </div>
  );
};

// Main App Component
function App() {
  const [user, setUser] = useState<User | null>(null);
  const [initialized, setInitialized] = useState(false);
  
  // Centralized auth state management
  const [authState, setAuthState] = useState<'idle' | 'initializing' | 'authenticated' | 'unauthenticated'>('idle');
  const authInitializedRef = useRef(false);
  const [betaInquiryModalOpen, setBetaInquiryModalOpen] = useState(false);
  const [generalInquiryModalOpen, setGeneralInquiryModalOpen] = useState(false);

  // ✅ REMOVED - LogRocket now handled in sequential initialization

  // URL parameter handling
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const shouldOpenBetaModal = urlParams.get('openBetaModal') === 'true';
    const shouldOpenGeneralModal = urlParams.get('openGeneralModal') === 'true';
    
    if (shouldOpenBetaModal) {
      setBetaInquiryModalOpen(true);
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    } else if (shouldOpenGeneralModal) {
      setGeneralInquiryModalOpen(true);
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, []);

  // 🔥 PRODUCTION-READY AUTH MANAGER - Clean, simple, no HMR hacks
  useEffect(() => {
    if (authInitializedRef.current) {
      return;
    }
    
    let authSubscription: any = null;
    let authChangeTimeout: ReturnType<typeof setTimeout> | null = null;
    
    const initializeAuth = async () => {
      console.log('🚀 Starting auth initialization...');
      authInitializedRef.current = true;
      setAuthState('initializing');
      
      try {
        // Initialize Supabase
        await initSupabase();
        console.log('✅ Supabase initialized');
        
        // Check existing session
        const sessionValidation = await authApi.validateSession();
        console.log('🔍 Session check:', sessionValidation.valid);
        
        if (sessionValidation.valid && sessionValidation.user) {
          setUser({
            id: sessionValidation.user.id,
            email: sessionValidation.user.email!,
          });
          setAuthState('authenticated');
        } else {
          setAuthState('unauthenticated');
        }
        
        // Set up auth state listener with improved debouncing
        const supabase = getSupabase();
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
          console.log('🔄 Auth state change:', event, !!session);
          
          // Clear any pending timeout
          if (authChangeTimeout !== null) {
            clearTimeout(authChangeTimeout);
            authChangeTimeout = null;
          }
          
          // Debounce rapid auth state changes
          authChangeTimeout = setTimeout(() => {
            if (session?.user) {
              setUser({
                id: session.user.id,
                email: session.user.email!,
              });
              setAuthState('authenticated');
            } else {
              setUser(null);
              setAuthState('unauthenticated');
            }
          }, 100); // 100ms debounce
        });

        authSubscription = subscription;
        setInitialized(true);
        console.log('🎉 Auth initialization complete');
        
      } catch (error) {
        console.error('❌ Auth initialization failed:', error);
        setAuthState('unauthenticated');
        setInitialized(true);
      }
    };

    initializeAuth();
    
    // Clean component unmount cleanup (production standard)
    return () => {
      if (authSubscription) {
        authSubscription.unsubscribe();
      }
      if (authChangeTimeout !== null) {
        clearTimeout(authChangeTimeout);
        authChangeTimeout = null;
      }
    };
  }, []);

  // ✅ User data initialization handled by MainApp component

  const handleLogout = async () => {
    try {
      // For logout, we'll use direct Supabase client since it's a core auth operation
      // TODO: Consider adding logout to authApi if needed
      const supabase = getSupabase();
      await supabase.auth.signOut();
      setUser(null);
    } catch (error) {
      console.error('Error during logout:', error);
    }
  };

  const handleOpenBetaInquiryModal = () => {
    setBetaInquiryModalOpen(true);
  };

  const handleOpenGeneralInquiryModal = () => {
    setGeneralInquiryModalOpen(true);
  };

  // Loading state
  if (!initialized) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <Router>
        <Routes>
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify" element={<Navigate to="/?verified=true" replace />} />
          <Route path="/dashboard/:tab?" element={
            user ? <MainApp user={user} onLogout={handleLogout} authState={authState} /> : <Navigate to="/" replace />
          } />
          <Route path="/project/:projectId/:tab?" element={
            user ? <MainApp user={user} onLogout={handleLogout} authState={authState} /> : <Navigate to="/" replace />
          } />
          <Route path="/" element={
            user ? <Navigate to="/dashboard/projects" /> : 
            <LandingPage onBetaInquiryClick={handleOpenBetaInquiryModal} onGeneralInquiryClick={handleOpenGeneralInquiryModal} />
          } />
        </Routes>
      </Router>
      
      <BetaInquiryModal 
        isOpen={betaInquiryModalOpen}
        onClose={() => setBetaInquiryModalOpen(false)} 
      />
      <GeneralInquiryModal 
        isOpen={generalInquiryModalOpen}
        onClose={() => setGeneralInquiryModalOpen(false)} 
      />
    </ErrorBoundary>
  );
}

export default App;